import { useEffect, useMemo } from "react";

import {
  getDashboardData,
  getReadDashboardArticles,
  setReadDashboardArticle,
} from "@/api/endpoints/dashboard";
import { getOrganizationUsers } from "@/api/endpoints/organization";
import Layout from "@/containers/Layout";
import { useOrganization } from "@/hooks/useOrganization";
import useSearchParams from "@/hooks/useSearchParams";
import { useUser } from "@/hooks/useUser";
import { User } from "@/types/User";
import AllEarsTitle from "@components/AllEarsTitle";
import Typography from "@components/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import ArticleModal from "./partials/ArticleModal";
import Articles from "./partials/Articles";
import Colleagues from "./partials/Colleagues";
import GettingStarted from "./partials/GettingStarted";
import LatestMentions from "./partials/LatestMentions";

import styles from "./Dashboard.module.css";

const DashboardView = () => {
  const queryClient = useQueryClient();

  // Global state
  const { data: orgData } = useOrganization();
  const { uuid: organizationId = null } = orgData?.ok ? orgData.data : {};
  const { data: user } = useUser();
  const { language = "en", first_name = "" } = (user?.data as User) || {};

  // URL state
  const [searchParams, setSearchParams] = useSearchParams<{
    article?: string;
  }>();

  // Server state
  const { data: usersData, error: usersError } = useQuery({
    queryKey: ["dashboard", "users", organizationId],
    queryFn: () => {
      if (!organizationId) return;
      return getOrganizationUsers(organizationId, { ordering: "-last_login" });
    },
  });

  const users = usersData?.ok ? usersData.data : undefined;

  const { data: dashboardData, error: dashboardDataError } = useQuery({
    queryKey: ["dashboard", "articles"],
    queryFn: getDashboardData,
  });

  const dashboardDataParsed = dashboardData?.ok
    ? dashboardData.data
    : undefined;

  const checklistArticles = dashboardDataParsed?.articles.checklist_articles;
  const tipsArticles = dashboardDataParsed?.articles.tips_articles;
  const helpArticles = dashboardDataParsed?.articles.help_articles;
  const recentEvents = dashboardDataParsed?.recent_events;

  const { data: readDashboardArticlesData } = useQuery({
    queryKey: ["dashboard", "articles", "read"],
    queryFn: getReadDashboardArticles,
  });

  const readDashboardArticles = readDashboardArticlesData?.ok
    ? readDashboardArticlesData.data.read_dashboard_articles
    : undefined;

  // Helpers
  const modalArticles = useMemo(
    () => [...(checklistArticles || []), ...(tipsArticles || [])],
    [checklistArticles, tipsArticles]
  );

  const modalArticle = useMemo(
    () =>
      searchParams.article
        ? modalArticles.find(
            ({ id }) => id === searchParams.article!.toString()
          )
        : undefined,
    [searchParams.article, modalArticles]
  );

  const nextArticle = useMemo(() => {
    const currentIndex = modalArticles.findIndex(
      (gma) => gma.id === searchParams.article?.toString()
    );
    return modalArticles[currentIndex + 1];
  }, [searchParams.article, modalArticles]);

  const { mutateAsync: mutateSetReadDashboardArticle } = useMutation({
    mutationFn: setReadDashboardArticle,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "articles", "read"],
      });
    },
  });

  // Side-effects
  useEffect(() => {
    if (searchParams.article) {
      mutateSetReadDashboardArticle(Number(searchParams.article));
    }
  }, [mutateSetReadDashboardArticle, searchParams.article]);

  return (
    <>
      <AllEarsTitle title={L.NAV_DASHBOARD} />

      <Layout>
        <div className={styles.dashboard}>
          <Typography variant="h1">
            {L.DASHBOARD_TITLE.replaceAll("{}", first_name)}
          </Typography>

          <div className={styles.dashboard__content}>
            <div className={styles["dashboard__content__left"]}>
              <GettingStarted
                checklistArticles={checklistArticles}
                tipsArticles={tipsArticles}
                articlesError={dashboardDataError}
                readDashboardArticles={readDashboardArticles}
              />
              <LatestMentions />
            </div>

            <div className={styles["dashboard__content__right"]}>
              <Colleagues
                recentEventsError={dashboardDataError}
                recentEvents={recentEvents}
                usersError={usersError}
                users={users}
              />
              <Articles
                helpArticlesError={dashboardDataError}
                helpArticles={helpArticles}
              />
            </div>
          </div>
        </div>
      </Layout>

      <ArticleModal
        title={modalArticle?.translated_content[language].title}
        description={modalArticle?.translated_content[language].body}
        nextArticle={nextArticle?.id}
        show={!!modalArticle}
        onHide={() => {
          setSearchParams({ ...searchParams, article: undefined });
        }}
      />
    </>
  );
};

export default DashboardView;
